import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, Eye, Calendar, AlertCircle, Loader2,
  Building2, ListChecks, Server, Activity, MapPin, Users,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, unauthenticated read-only snapshot viewer.
 * Reachable at /snapshot/:token — no login required.
 * Renders a sanitized, time-boxed view of a demo tenant for prospects.
 */
export default function SnapshotViewerPage() {
  const { token } = useParams();
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/snapshot/${token}`);
        if (!cancelled) setSnap(res.data);
      } catch (e) {
        if (!cancelled) {
          const code = e?.response?.status;
          if (code === 404) setError("This snapshot link is invalid or has been revoked.");
          else if (code === 410) setError("This snapshot has expired. Please request a fresh share link.");
          else setError(extractErrorMessage(e, "Unable to load snapshot."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6">
        <AlertCircle className="w-10 h-10 text-rose-500 mb-3" />
        <h1 className="text-xl font-semibold text-slate-800 mb-2">Snapshot unavailable</h1>
        <p className="text-slate-600 text-center max-w-md">{error}</p>
        <Link to="/" className="mt-6 text-sky-600 hover:underline text-sm">← Back to ResiliencyHUB</Link>
      </div>
    );
  }

  const data = snap?.data || {};
  const summary = data._summary || {};
  const expires = snap?.expires_at ? new Date(snap.expires_at) : null;

  const Section = ({ icon: Icon, title, items, render }) => (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid={`snapshot-section-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-sky-600" />
        <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
        <span className="text-xs text-slate-500 ml-auto">{(items || []).length} records</span>
      </div>
      {(items || []).length === 0 ? (
        <p className="text-sm text-slate-400 italic">No data captured.</p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <tbody>{(items || []).slice(0, 8).map((it, i) => render(it, i))}</tbody>
          </table>
          {items.length > 8 && (
            <p className="text-xs text-slate-400 mt-3 px-2">+ {items.length - 8} more (full list available in live demo)</p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50">
      {/* Watermark banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-xs text-amber-800">
        <ShieldCheck className="w-3 h-3 inline-block mr-1.5" />
        {snap?.watermark || "Read-Only Evaluation Snapshot · Powered by ResiliencyHUB"}
        {expires && (
          <span className="ml-3 text-amber-700">
            · Expires {expires.toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        {/* Header */}
        <div className="mb-8 bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs text-sky-600 font-semibold uppercase tracking-wide mb-1">
                Mission Control · Read-Only Evaluation
              </p>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{snap?.label || "Demo Snapshot"}</h1>
              <p className="text-sm text-slate-600">
                Industry: <span className="font-medium text-slate-800 capitalize">{snap?.industry || summary.industry || "general"}</span>
                {snap?.created_at && (
                  <>
                    <span className="mx-2">·</span>
                    Generated {new Date(snap.created_at).toLocaleDateString()}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Eye className="w-4 h-4" />
              {snap?.view_count || 0} view{(snap?.view_count || 0) === 1 ? "" : "s"}
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi label="Processes (BIA)" value={summary.total_processes ?? 0} />
            <Kpi label="RA Tasks" value={summary.total_ra_tasks ?? 0} />
            <Kpi label="Applications" value={summary.total_applications ?? 0} />
            <Kpi label="Exercises" value={summary.total_exercises ?? 0} />
            <Kpi label="Departments" value={summary.total_departments ?? 0} />
            <Kpi label="Facilities" value={summary.total_facilities ?? 0} />
          </div>
        </div>

        {/* Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section
            icon={ListChecks}
            title="BIA Processes"
            items={data.process_bia}
            render={(p, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-800">{p.process_name || "Process"}</td>
                <td className="py-2 pr-3 text-xs text-slate-500">{p.department || "—"}</td>
                <td className="py-2 text-xs text-right">
                  <span className={`inline-block px-2 py-0.5 rounded-full ${p.criticality_tier === 1 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                    Tier {p.criticality_tier ?? "—"}
                  </span>
                </td>
              </tr>
            )}
          />
          <Section
            icon={Activity}
            title="Active RA Tasks"
            items={data.ra_tasks}
            render={(t, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-800">{t.asset_name || "Asset"}</td>
                <td className="py-2 pr-3 text-xs text-slate-500 capitalize">{t.asset_ref_type || "—"}</td>
                <td className="py-2 text-xs text-right capitalize text-slate-600">
                  {t.status || "—"}
                  {typeof t.residual_risk === "number" && (
                    <span className="ml-2 text-[11px] text-slate-400">R: {t.residual_risk}</span>
                  )}
                </td>
              </tr>
            )}
          />
          <Section
            icon={Server}
            title="Applications"
            items={data.application_profiles}
            render={(a, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-800">{a.application_name || "App"}</td>
                <td className="py-2 pr-3 text-xs text-slate-500">RTO: {a.rto_hours ?? "—"}h</td>
                <td className="py-2 text-xs text-right text-slate-500">RPO: {a.rpo_hours ?? "—"}h</td>
              </tr>
            )}
          />
          <Section
            icon={Calendar}
            title="Scheduled Exercises"
            items={data.exercises}
            render={(e, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-800">{e.title || "Exercise"}</td>
                <td className="py-2 pr-3 text-xs text-slate-500 capitalize">{e.exercise_type || e.type || "—"}</td>
                <td className="py-2 text-xs text-right capitalize text-slate-600">{e.status || "—"}</td>
              </tr>
            )}
          />
          <Section
            icon={Users}
            title="Departments"
            items={data.departments}
            render={(d, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-800">{d.name || "Dept"}</td>
                <td className="py-2 text-xs text-right capitalize text-slate-600">{d.criticality || "—"}</td>
              </tr>
            )}
          />
          <Section
            icon={MapPin}
            title="Facilities"
            items={data.facilities}
            render={(f, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-800">{f.name || "Facility"}</td>
                <td className="py-2 pr-3 text-xs text-slate-500">{f.city || "—"}</td>
                <td className="py-2 text-xs text-right capitalize text-slate-600">{f.criticality || "—"}</td>
              </tr>
            )}
          />
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-500">
          <p className="mb-2">
            <Building2 className="w-3 h-3 inline-block mr-1" />
            All names, emails, and identifiers in this snapshot have been pseudonymized.
          </p>
          <p>
            Want a live walkthrough?{" "}
            <Link to="/contact" className="text-sky-600 hover:underline" data-testid="snapshot-request-demo-link">
              Request a personalized demo →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-md p-3 border border-slate-200" data-testid={`snapshot-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}
